// src/pages/Glossary/Glossary.tsx

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './Glossary.module.css';

/* ── Term data ─────────────────────────────────────────── */

interface Term {
  name: string;
  aka?: string;
  short: string;
  detail: string;
  botUsage?: string;
}

interface Section {
  title: string;
  emoji: string;
  terms: Term[];
}

const SECTIONS: Section[] = [
  {
    title: 'Technical Indicators',
    emoji: '📊',
    terms: [
      {
        name: 'RSI',
        aka: 'Relative Strength Index',
        short: 'Measures how overbought or oversold a stock is on a 0–100 scale.',
        detail:
          'RSI compares the average size of recent gains to recent losses over a period (usually 14 bars). Above 70 is considered overbought (price may be too high), below 30 is oversold (price may be too low). It helps identify when a move might be running out of steam.',
        botUsage:
          'Used in the composite signal score. The pullback bot looks for RSI < 35 as one of its entry conditions. The options bot uses RSI to gauge momentum strength.',
      },
      {
        name: 'EMA',
        aka: 'Exponential Moving Average',
        short: 'A smoothed average of recent prices that reacts faster to new data than a simple average.',
        detail:
          "Unlike a Simple Moving Average (SMA) which weights all prices equally, the EMA gives more weight to recent prices. Common periods are EMA-9 (very fast, for scalping), EMA-20 (short-term trend), EMA-50 (medium-term), and EMA-200 (long-term trend). When a shorter EMA crosses above a longer one, it's a bullish signal (and vice versa).",
        botUsage:
          'The multi-stock scalper uses EMA-9/20 crossovers for entry signals. The options bot checks whether price is above/below the 20, 50, and 200 EMAs to determine the daily trend (BULL / BEAR / CHOP).',
      },
      {
        name: 'VWAP',
        aka: 'Volume Weighted Average Price',
        short: 'The average price weighted by volume — shows the "fair price" for the day.',
        detail:
          'VWAP is calculated by taking the total dollar value traded (price × volume for each trade) divided by total volume. It resets each day. Institutional traders use VWAP as a benchmark — buying below VWAP is considered "good" execution. Price above VWAP = bullish intraday bias, below = bearish.',
        botUsage:
          "The multi-stock scalper uses VWAP as a key directional filter. The governor module checks SPY's position relative to VWAP to determine market state (BULL/BEAR).",
      },
      {
        name: 'ATR',
        aka: 'Average True Range',
        short: 'Measures how much a stock typically moves in a given period — a volatility gauge.',
        detail:
          'True Range is the greatest of: current high minus low, absolute value of high minus previous close, or absolute value of low minus previous close. ATR averages this over N periods (usually 14). A stock with ATR of $2 on a $100 stock moves about 2% per day. Higher ATR = more volatile.',
        botUsage:
          'Used to set dynamic stop-losses and take-profit levels. Instead of a fixed dollar stop, bots use multiples of ATR (e.g., stop at 1.5× ATR below entry). This adapts to market conditions automatically.',
      },
      {
        name: 'Bollinger Bands',
        short: 'Bands drawn 2 standard deviations above and below a moving average — shows volatility range.',
        detail:
          'The middle band is typically a 20-period SMA. Upper and lower bands are ±2 standard deviations. When bands narrow ("squeeze"), a big move is coming. %B measures where price is relative to the bands: 0 = at lower band, 1 = at upper band, negative = below lower band. Values below 0 suggest oversold conditions.',
        botUsage:
          'The pullback bot uses Bollinger %B < 0 as a key oversold signal. When price drops below the lower band, it suggests a potential mean reversion opportunity.',
      },
      {
        name: 'MACD',
        aka: 'Moving Average Convergence Divergence',
        short: 'Shows the relationship between two EMAs — used to spot momentum shifts.',
        detail:
          'MACD is calculated as EMA-12 minus EMA-26. A "signal line" (EMA-9 of the MACD) is then plotted on top. When MACD crosses above the signal line, it\'s bullish. The histogram shows the difference between MACD and its signal line — growing bars mean momentum is increasing.',
        botUsage:
          'Part of the composite signal scoring in indicators.py. MACD histogram direction contributes to the momentum sub-score.',
      },
      {
        name: 'Volume',
        short: 'The number of shares or contracts traded in a period.',
        detail:
          'High volume confirms price moves — a breakout on heavy volume is more reliable than one on light volume. Relative volume (RVOL) compares current volume to the average for that time of day. RVOL > 1.5 means notably higher-than-normal activity.',
        botUsage:
          'The multi-stock scanner ranks stocks by relative volume to find the most active names. Volume confirmation is part of the signal scoring system.',
      },
    ],
  },
  {
    title: 'Market Concepts',
    emoji: '🏛️',
    terms: [
      {
        name: 'VIX',
        aka: 'CBOE Volatility Index',
        short: 'The "fear gauge" — measures expected market volatility over the next 30 days.',
        detail:
          'VIX is derived from S&P 500 option prices. A VIX of 15 means the market expects about 15% annualized volatility. Below 15 = calm/complacent, 15–25 = normal, 25–35 = elevated fear, above 35 = panic. High VIX makes options more expensive (higher premiums) and signals wider price swings.',
        botUsage:
          'The prediction brain uses VIX level as a key input. The pullback bot monitors VIX changes for entry signals. Options strategies are adjusted based on VIX regime.',
      },
      {
        name: 'Support & Resistance',
        short: 'Price levels where buying (support) or selling (resistance) pressure tends to appear.',
        detail:
          'Support is a price floor where buyers step in, preventing further decline. Resistance is a ceiling where sellers emerge. These levels form from previous highs/lows, round numbers, and moving averages. When support breaks, it often becomes resistance (and vice versa).',
        botUsage:
          'The options bot calculates 20-day support/resistance zones to place option strike prices safely outside the expected range.',
      },
      {
        name: 'Trend',
        short: 'The general direction of price movement — BULL (up), BEAR (down), or CHOP (sideways).',
        detail:
          "A bull trend has higher highs and higher lows. A bear trend has lower highs and lower lows. Chop/range-bound means price oscillates without clear direction. Trend is determined by comparing EMAs: if 20 EMA > 50 EMA > 200 EMA, that's a strong bull trend.",
        botUsage:
          'The governor module classifies SPY into BULL/BEAR/CHOP states every 15 seconds. This determines which strategies the bots use — e.g., credit call spreads in bear trends, iron condors in chop.',
      },
      {
        name: 'Gap',
        short: 'When a stock opens significantly higher or lower than the previous close.',
        detail:
          "A gap up means the open price is above yesterday's close (bullish overnight sentiment). A gap down means the open is below yesterday's close. Gaps often partially fill during the day as prices pull back toward the previous close. Gap percentage is calculated as (open − prev_close) / prev_close × 100.",
        botUsage:
          'The prediction brain analyzes SPY gap percentage as an input to the daily forecast. Large gaps can signal trend continuation or exhaustion.',
      },
      {
        name: 'Mean Reversion',
        short: 'The idea that prices tend to return to their average after extreme moves.',
        detail:
          'When a stock drops sharply below its moving average or Bollinger Band, mean reversion suggests it will bounce back. This is the opposite of momentum/trend-following. It works best in range-bound markets and can be dangerous in strong trends.',
        botUsage:
          'The pullback reversion bot is built entirely on this concept — it buys SPY after oversold conditions and exits when price reverts to the mean (typically 3-day holds).',
      },
      {
        name: 'Market Regime',
        short: 'The current "mode" of the market — trending, range-bound, or volatile.',
        detail:
          'Different strategies work in different regimes. Trend-following works when markets are directional. Mean reversion works in ranges. Options selling works in low-to-moderate volatility. Identifying the regime helps pick the right strategy.',
        botUsage:
          'The indicators.py module classifies regimes as TRENDING, RANGE_BOUND, or VOLATILE. The options bot maps regimes to strategy types (iron condors in range, directional spreads in trends).',
      },
    ],
  },
  {
    title: 'Options Trading',
    emoji: '🎯',
    terms: [
      {
        name: 'Options Contract',
        short: 'A contract giving the right (not obligation) to buy or sell a stock at a set price by a set date.',
        detail:
          'A call option gives the right to BUY at the strike price. A put option gives the right to SELL at the strike price. Each contract represents 100 shares. You pay a "premium" to buy an option. Options have an expiration date — after that, they\'re worthless if not exercised.',
      },
      {
        name: 'Strike Price',
        short: 'The price at which an option can be exercised.',
        detail:
          'For a call with a $670 strike, you can buy shares at $670 regardless of market price. For a put with a $670 strike, you can sell at $670. "In the money" (ITM) means the option has intrinsic value: calls when stock > strike, puts when stock < strike. "Out of the money" (OTM) means it currently has no intrinsic value.',
      },
      {
        name: 'Premium',
        short: 'The price paid (or received) for an option contract.',
        detail:
          "Premium has two components: intrinsic value (how much it's ITM) and extrinsic/time value (probability it could become ITM). A premium of $2.50 means $250 per contract (100 shares × $2.50). When you sell options, you collect premium — that's your max profit.",
        botUsage:
          'The options bot targets credit strategies where you collect premium upfront. Max profit = total premium collected.',
      },
      {
        name: 'Iron Condor',
        short: 'A neutral strategy that profits when the stock stays within a range.',
        detail:
          'Combines a bull put spread (sell a put, buy a lower put) and a bear call spread (sell a call, buy a higher call). You collect premium from both sides. Max profit = total premium collected. Max loss = width of either spread minus premium. Wins when price stays between the short strikes.',
        botUsage:
          'The options bot places iron condors in CHOP/range-bound regimes when the composite score is near zero (no strong directional bias).',
      },
      {
        name: 'Credit Spread',
        short: 'Selling a more expensive option and buying a cheaper one — you collect the difference as credit.',
        detail:
          'Bull put spread: sell a higher put, buy a lower put. Profits if stock stays above the short strike. Bear call spread: sell a lower call, buy a higher call. Profits if stock stays below the short strike. Max profit = credit received. Max loss = spread width minus credit.',
        botUsage:
          'The options bot uses bear call spreads in BEAR regimes and bull put spreads in BULL regimes, aligning the directional bias with the trend.',
      },
      {
        name: 'IV',
        aka: 'Implied Volatility',
        short: "The market's forecast of how much a stock will move — baked into option prices.",
        detail:
          'Higher IV = more expensive options. IV Rank compares current IV to its 52-week range (0–100). High IV Rank means options are expensive relative to history — better for selling. IV Crush happens after events (earnings) when uncertainty drops and IV collapses, crushing option prices.',
        botUsage:
          'The options bot takes IV snapshots and avoids selling options when IV is unusually low (less premium to collect).',
      },
      {
        name: 'Greeks',
        short: 'Measurements of how option prices change relative to different factors.',
        detail:
          'Delta: how much the option price moves per $1 stock move (0 to 1 for calls, 0 to -1 for puts). Gamma: rate of change of delta. Theta: time decay — how much value the option loses per day. Vega: sensitivity to implied volatility changes. For sellers, theta is your friend (decay works in your favor).',
        botUsage:
          'The options bot monitors delta for position management. If net delta exceeds ±0.3, it triggers a breach exit to manage directional risk.',
      },
      {
        name: '0DTE',
        aka: 'Zero Days to Expiration',
        short: "Options that expire the same day they're traded — maximum time decay, maximum risk.",
        detail:
          'With 0DTE options, theta decay is extreme — options lose value rapidly throughout the day. This benefits sellers but moves can be violent. SPY has 0DTE options every trading day (Mon–Fri expirations). Small price moves create large percentage gains/losses.',
        botUsage:
          'The spy_0dte_scalper is designed specifically for these — quick in-and-out trades capturing rapid premium decay on same-day SPY options.',
      },
    ],
  },
  {
    title: 'Risk & Performance Metrics',
    emoji: '📈',
    terms: [
      {
        name: 'Win Rate',
        short: 'Percentage of trades that are profitable.',
        detail:
          "Win rate alone doesn't tell the full story. A 40% win rate can be very profitable if average wins are 3× average losses. A 90% win rate can lose money if the losses are enormous. Always pair win rate with profit factor or average win/loss ratio.",
        botUsage:
          'Tracked for every bot on the dashboard. The pullback bot targets 60%+ win rate with modest per-trade gains.',
      },
      {
        name: 'Profit Factor',
        short: 'Total gross profits divided by total gross losses. Above 1.0 = making money.',
        detail:
          'Profit factor of 1.5 means for every $1 lost, you make $1.50. Below 1.0 means the strategy is losing money. 1.0–1.5 = marginal edge, 1.5–2.0 = solid, above 2.0 = excellent. It naturally accounts for both win rate and win/loss size.',
        botUsage:
          'Key metric in all backtests. The pullback strategy showed profit factor of 1.64 out-of-sample — a solid edge.',
      },
      {
        name: 'Sharpe Ratio',
        short: 'Risk-adjusted return — how much return you get per unit of risk taken.',
        detail:
          'Calculated as (average return − risk-free rate) / standard deviation of returns. A Sharpe of 1.0 means you earn 1 unit of return per unit of volatility. Below 0.5 = poor, 0.5–1.0 = acceptable, 1.0–2.0 = good, above 2.0 = excellent. Higher is better — it means more return for the same risk.',
        botUsage:
          'Computed in all backtests. The pullback strategy showed Sharpe of 1.82 out-of-sample — indicating strong risk-adjusted performance.',
      },
      {
        name: 'Max Drawdown',
        short: 'The largest peak-to-trough decline in account value — your worst-case scenario.',
        detail:
          "If your account goes from $10,000 to $8,000 before recovering, that's a 20% max drawdown. It measures the worst pain you'd experience. Strategies with low Sharpe ratios tend to have large drawdowns. A 50% drawdown requires a 100% gain just to break even.",
        botUsage:
          'Tracked in backtests and monitored by the portfolio guard. The equity floor ($10K) acts as an absolute drawdown limit across all bots.',
      },
      {
        name: 'Equity Curve',
        short: 'A chart showing your account value over time — should trend upward and to the right.',
        detail:
          "A smooth, upward-sloping equity curve indicates a consistent edge. A choppy or declining curve means the strategy isn't working. Comparing equity curves across different time periods (in-sample vs out-of-sample) helps validate that the edge is real and not just curve-fitting.",
        botUsage:
          'The dashboard shows a combined equity curve for all bots. Each backtest also produces its own equity curve for analysis.',
      },
      {
        name: 'Stop Loss',
        short: 'A pre-set price level where you automatically exit a losing trade to limit damage.',
        detail:
          'A stop loss at 2% means if the position drops 2% from entry, you sell immediately. Trailing stops move up as the stock rises, locking in profits. ATR-based stops adapt to volatility — wider stops in volatile markets, tighter in calm ones.',
        botUsage:
          'All bots use dynamic stop losses based on ATR multiples. The options bot uses a 2× credit stop (if losses reach 2× the premium collected, it exits).',
      },
      {
        name: 'Position Sizing',
        short: 'How much capital to allocate to each trade — the most important risk management tool.',
        detail:
          'Risk a fixed percentage of equity per trade (typically 1–2%). If your account is $25,000 and you risk 1%, your max loss per trade is $250. This ensures no single trade can blow up your account. Smaller positions = more trades needed to grow, but much more survivable.',
        botUsage:
          'The portfolio guard enforces max total exposure (25% of equity) and max correlated exposure (15%). Individual bots have their own position size limits.',
      },
      {
        name: 'PDT Rule',
        aka: 'Pattern Day Trader',
        short: 'SEC rule: accounts under $25K are limited to 3 day trades per 5 business days.',
        detail:
          'A day trade is buying and selling the same security on the same day. If you make 4+ day trades in 5 business days with under $25K equity, your account gets flagged and restricted for 90 days. Options are included. The workaround is to hold positions overnight or fund the account above $25K.',
        botUsage:
          "The pdt_guard.py module tracks day trade counts and blocks orders that would trigger a 4th day trade. It's integrated into the multi-stock scalper, options bot, and 0DTE scalper.",
      },
    ],
  },
  {
    title: 'Bot-Specific Concepts',
    emoji: '🤖',
    terms: [
      {
        name: 'Composite Score',
        short: 'A combined numerical score from multiple indicators that drives trading decisions.',
        detail:
          'Rather than relying on a single indicator, the composite score weighs trend, momentum, volatility, and volume sub-scores into one number. A positive score = bullish bias, negative = bearish, near zero = neutral/chop. The magnitude indicates confidence.',
        botUsage:
          'Computed by indicators.py score_signals(). The options bot maps score ranges to strategy types: high positive → bull put spread, high negative → bear call spread, near zero → iron condor.',
      },
      {
        name: 'Governor',
        short: 'A module that monitors SPY in real-time and classifies the market state.',
        detail:
          "The governor runs every 15 seconds, checking SPY's position relative to VWAP and EMAs. It outputs a state: BULL (above VWAP + EMAs trending up), BEAR (below VWAP + EMAs trending down), or CHOP (mixed signals). This state influences which trades the scalper takes.",
        botUsage:
          "The multi-stock scalper uses the governor state to filter entries. In BEAR state, it avoids long entries on weak setups. In BULL state, it's more aggressive on long setups.",
      },
      {
        name: 'Circuit Breaker',
        short: 'An automatic kill switch that halts all trading when losses exceed a threshold.',
        detail:
          'When the daily loss limit is hit, a lockout file is written to disk. All bots check for this file before placing orders. It prevents compounding losses across multiple bots during a bad day. The lockout automatically expires the next trading day.',
        botUsage:
          'Implemented in portfolio_guard.py. If total daily losses across all bots exceed 2% of equity, the circuit breaker triggers and blocks all new orders until the next day.',
      },
      {
        name: 'Prediction Brain',
        short: 'A daily pre-market analysis that forecasts market direction and confidence.',
        detail:
          "Runs before market open via a systemd timer. Analyzes VIX, SPY gaps, news sentiment, trend indicators, and yesterday's bot performance. Outputs a direction (BULL/BEAR), confidence score, and risk flags. The prediction is stored and later compared to actual market movement for accuracy tracking.",
        botUsage:
          'Results are published to S3 and displayed on the Predictions page. The accuracy of predictions is tracked on the Prediction Accuracy page.',
      },
      {
        name: 'S3 Sync',
        short: 'Automatic upload of bot data to AWS S3 for display on the website dashboard.',
        detail:
          'The dashboard_s3_sync.py script runs on a timer (every 5 minutes during market hours). It collects trade data, positions, equity curves, and prediction results from the bots and pushes JSON files to an S3 bucket. The React website fetches these JSON files to show real-time dashboards.',
        botUsage:
          'Powers all the dashboard pages — overview, trades, positions, predictions, prediction accuracy, and bot performance.',
      },
    ],
  },
];

/* ── Component ─────────────────────────────────────────── */

const Glossary: React.FC = () => {
  const [expandedTerms, setExpandedTerms] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');

  const toggleTerm = (key: string) => {
    setExpandedTerms((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const expandAll = () => {
    const all = new Set<string>();
    SECTIONS.forEach((s) => s.terms.forEach((t) => all.add(`${s.title}-${t.name}`)));
    setExpandedTerms(all);
  };

  const collapseAll = () => setExpandedTerms(new Set());

  const lowerFilter = filter.toLowerCase();
  const filteredSections = SECTIONS.map((section) => ({
    ...section,
    terms: section.terms.filter(
      (t) =>
        t.name.toLowerCase().includes(lowerFilter) ||
        t.short.toLowerCase().includes(lowerFilter) ||
        (t.aka && t.aka.toLowerCase().includes(lowerFilter)),
    ),
  })).filter((s) => s.terms.length > 0);

  const totalTerms = SECTIONS.reduce((sum, s) => sum + s.terms.length, 0);

  return (
    <section className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>📖 Market Glossary</h1>
        <p className={styles.subtitle}>
          {totalTerms} terms covering the indicators, strategies, and concepts used across all bots
        </p>
      </header>

      <div className={styles.controls}>
        <input
          type="text"
          className={styles.search}
          placeholder="Search terms… (e.g. RSI, stop loss, iron condor)"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          title="Search glossary terms"
        />
        <div className={styles.buttons}>
          <button className={styles.btn} onClick={expandAll}>
            Expand All
          </button>
          <button className={styles.btn} onClick={collapseAll}>
            Collapse All
          </button>
        </div>
      </div>

      {filteredSections.length === 0 && <p className={styles.noResults}>No terms match "{filter}"</p>}

      {filteredSections.map((section) => (
        <div key={section.title} className={styles.section}>
          <h2 className={styles.sectionTitle}>
            {section.emoji} {section.title}
          </h2>
          <div className={styles.termList}>
            {section.terms.map((term) => {
              const key = `${section.title}-${term.name}`;
              const isOpen = expandedTerms.has(key);
              return (
                <div key={key} className={`${styles.term} ${isOpen ? styles.termOpen : ''}`}>
                  <button className={styles.termHeader} onClick={() => toggleTerm(key)} aria-expanded={isOpen}>
                    <div className={styles.termNameRow}>
                      <span className={styles.termName}>{term.name}</span>
                      {term.aka && <span className={styles.termAka}>{term.aka}</span>}
                    </div>
                    <span className={styles.chevron}>{isOpen ? '▲' : '▼'}</span>
                  </button>
                  <p className={styles.termShort}>{term.short}</p>
                  {isOpen && (
                    <div className={styles.termDetail}>
                      <p>{term.detail}</p>
                      {term.botUsage && (
                        <div className={styles.botUsage}>
                          <span className={styles.botUsageLabel}>🤖 How our bots use it:</span>
                          <p>{term.botUsage}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <Link to="/home" className={styles.back}>
        ← Back to Dashboard
      </Link>
    </section>
  );
};

export default Glossary;
